import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import { backupServer, server } from "../../server";
import LoadingComponent from "../../Components/LoadingComponent";
import {
  MdCloudDone,
  MdOutlineSecurity,
  MdStorage,
  MdRefresh,
  MdComputer,
  MdCheckCircle,
  MdErrorOutline,
  MdWarning,
  MdSearch,
  MdContentCopy,
  MdOutlineCloudSync,
  MdOutlineSpeed,
  MdOutlineAnalytics,
  MdOutlineFingerprint,
  MdClose,
  MdFilterList,
  MdVerified,
  MdOutlineCalendarToday,
  MdLockOutline,
  MdExpandMore,
  MdExpandLess,
  MdOutlineInfo,
} from "react-icons/md";
import {
  RiShieldCheckLine,
  RiFolderZipLine,
  RiTimeLine,
  RiHistoryLine,
  RiShieldCheckFill,
  RiStackLine,
} from "react-icons/ri";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Line,
  Cell,
} from "recharts";

// Default fallback data for Dashboard endpoint (/api/v1/backups/dashboard)
const INITIAL_MOCK_DATA = {
  success: true,
  timestamp: "2026-08-24T08:49:53.0885114Z",
  systemHealth: {
    status: "Optimal",
    threatRiskScore: 0,
    scannerEnabled: true,
    lastBackupReceivedAt: "2026-08-24T06:57:15.779274Z",
    serverDiskStorage: {
      driveName: "C:\\",
      freeSpaceBytes: 2578882547712,
      freeSpaceFormatted: "2.35 TB",
      totalSpaceBytes: 2699361775616,
      totalSpaceFormatted: "2.46 TB",
      usedPercentage: "4.46%",
    },
  },
  kpi: {
    totalBackups: 64,
    cleanBackups: 64,
    infectedBackups: 0,
    failedBackups: 0,
    activeClients: 15,
    totalStorageBytes: 5501072883,
    totalStorageFormatted: "5.12 GB",
    averageBackupSizeBytes: 85954263,
    averageBackupSizeFormatted: "81.97 MB",
    largestBackupSizeBytes: 295685828,
    largestBackupSizeFormatted: "281.99 MB",
    threatDetectionRate: "0%",
    dailyStorageVelocityFormatted: "174.87 MB/day",
  },
  threatIntelligence: {
    threatRiskScore: 0,
    totalThreatsIntercepted: 0,
    extensionBreakdown: [
      {
        extension: ".zip",
        totalFiles: 64,
        infectedFiles: 0,
        totalBytesFormatted: "5.12 GB",
      },
    ],
    recentThreats: [],
  },
  clientNodeHealth: {
    totalNodes: 15,
    onlineNodes24h: 2,
    idleNodes: 13,
    clientAnalytics: [
      {
        clientIdentifier: "2026-08",
        isOnline24h: true,
        totalBackups: 8,
        cleanBackups: 8,
        infectedBackups: 0,
        totalBytes: 49262224,
        totalBytesFormatted: "46.98 MB",
        lastBackupAt: "2026-08-24T06:45:59.080384Z",
      },
      {
        clientIdentifier: "80.13",
        isOnline24h: true,
        totalBackups: 2,
        cleanBackups: 2,
        infectedBackups: 0,
        totalBytes: 12315698,
        totalBytesFormatted: "11.75 MB",
        lastBackupAt: "2026-08-24T06:57:15.779274Z",
      },
      {
        clientIdentifier:
          "C:\\AutoBackups\\80.13\\Assets\\AssetsBackup_2026-08-22.zip",
        isOnline24h: false,
        totalBackups: 1,
        cleanBackups: 1,
        infectedBackups: 0,
        totalBytes: 122436,
        totalBytesFormatted: "119.57 KB",
        lastBackupAt: "2026-08-22T12:18:59.604413Z",
      },
      {
        clientIdentifier:
          "C:\\AutoBackups\\80.13\\Database\\DatabaseBackup_2026-08-22.zip",
        isOnline24h: false,
        totalBackups: 1,
        cleanBackups: 1,
        infectedBackups: 0,
        totalBytes: 6144150,
        totalBytesFormatted: "5.86 MB",
        lastBackupAt: "2026-08-22T12:19:00.237096Z",
      },
      {
        clientIdentifier:
          "C:\\AutoBackups\\2026-08\\20260822_121859_1aa75efe_AssetsBackup_2026-08-22.zip",
        isOnline24h: false,
        totalBackups: 1,
        cleanBackups: 1,
        infectedBackups: 0,
        totalBytes: 122436,
        totalBytesFormatted: "119.57 KB",
        lastBackupAt: "2026-08-22T12:19:13.506721Z",
      },
      {
        clientIdentifier:
          "C:\\AutoBackups\\2026-08\\20260822_121859_a40eaf3d_DatabaseBackup_2026-08-22.zip",
        isOnline24h: false,
        totalBackups: 1,
        cleanBackups: 1,
        infectedBackups: 0,
        totalBytes: 6144150,
        totalBytesFormatted: "5.86 MB",
        lastBackupAt: "2026-08-22T12:19:13.845318Z",
      },
    ],
  },
  hourlyHeatmap: [
    {
      hour: "06:00",
      hourNumber: 6,
      backupCount: 10,
      totalBytesFormatted: "58.73 MB",
    },
    {
      hour: "12:00",
      hourNumber: 12,
      backupCount: 18,
      totalBytesFormatted: "53.79 MB",
    },
    {
      hour: "17:00",
      hourNumber: 17,
      backupCount: 36,
      totalBytesFormatted: "5.01 GB",
    },
  ],
  timeSeriesTrend: [
    {
      date: "2026-08-22",
      cleanCount: 36,
      infectedCount: 0,
      failedCount: 0,
      totalBytes: 2722945464,
      totalBytesFormatted: "2.54 GB",
    },
    {
      date: "2026-08-23",
      cleanCount: 18,
      infectedCount: 0,
      failedCount: 0,
      totalBytes: 2716549497,
      totalBytesFormatted: "2.53 GB",
    },
    {
      date: "2026-08-24",
      cleanCount: 10,
      infectedCount: 0,
      failedCount: 0,
      totalBytes: 61577922,
      totalBytesFormatted: "58.73 MB",
    },
  ],
  recentActivity: [
    {
      id: 298,
      fileName: "DatabaseBackup_2026-08-24.zip",
      fileSizeBytes: 6157920,
      fileSizeFormatted: "5.87 MB",
      status: "Completed",
      sourceClient: "80.13",
      clientIpAddress: "80.13",
      receivedAt: "2026-08-24T06:57:15.779274Z",
    },
    {
      id: 297,
      fileName:
        "20260824_064545_0ec19a1b_20260824_064531_5d9d997d_20260824_064517_45523f5f_DatabaseBackup_2026-08-24.zip",
      fileSizeBytes: 6157778,
      fileSizeFormatted: "5.87 MB",
      status: "Completed",
      sourceClient: "2026-08",
      clientIpAddress: "2026-08",
      receivedAt: "2026-08-24T06:45:59.080384Z",
    },
  ],
};

// Default fallback data for Analytics endpoint (/api/v1/backups/analytics)
const INITIAL_ANALYTICS_DATA = {
  success: true,
  periodDays: 7,
  startDate: "2026-08-18",
  endDate: "2026-08-24",
  summary: {
    totalBackups: 64,
    totalClean: 64,
    totalInfected: 0,
    totalFailed: 0,
    totalBytes: 5501072883,
    totalBytesFormatted: "5.12 GB",
  },
  chartData: [
    {
      date: "2026-08-18",
      displayDate: "18 Aug",
      cleanCount: 0,
      infectedCount: 0,
      failedCount: 0,
      totalCount: 0,
      sizeBytes: 0,
      sizeFormatted: "0.00 B",
      sizeInMB: 0,
      sizeInGB: 0,
      records: [],
    },
    {
      date: "2026-08-19",
      displayDate: "19 Aug",
      cleanCount: 0,
      infectedCount: 0,
      failedCount: 0,
      totalCount: 0,
      sizeBytes: 0,
      sizeFormatted: "0.00 B",
      sizeInMB: 0,
      sizeInGB: 0,
      records: [],
    },
    {
      date: "2026-08-20",
      displayDate: "20 Aug",
      cleanCount: 0,
      infectedCount: 0,
      failedCount: 0,
      totalCount: 0,
      sizeBytes: 0,
      sizeFormatted: "0.00 B",
      sizeInMB: 0,
      sizeInGB: 0,
      records: [],
    },
    {
      date: "2026-08-21",
      displayDate: "21 Aug",
      cleanCount: 0,
      infectedCount: 0,
      failedCount: 0,
      totalCount: 0,
      sizeBytes: 0,
      sizeFormatted: "0.00 B",
      sizeInMB: 0,
      sizeInGB: 0,
      records: [],
    },
    {
      date: "2026-08-22",
      displayDate: "22 Aug",
      cleanCount: 36,
      infectedCount: 0,
      failedCount: 0,
      totalCount: 36,
      sizeBytes: 2722945464,
      sizeFormatted: "2.54 GB",
      sizeInMB: 2596.8,
      sizeInGB: 2.536,
      records: [
        {
          id: 270,
          fileName:
            "20260822_173453_17a1c1b2_20260822_173429_603f7aa0_20260822_173406_c0c00b81_AssetsBackup_2026-08-22.zip",
          fileSizeBytes: 980547,
          fileSizeFormatted: "0.94 MB",
          status: "Completed",
          checksumSHA256:
            "59D665324B6B76C37F1E51BB71E59C19A7DF40DB49222E457923B650DDD227FE",
          sourceClient: "C:\\AutoBackups\\2026-08\\AssetsBackup_2026-08-22.zip",
          receivedAt: "2026-08-22T17:35:13.201261Z",
        },
        {
          id: 269,
          fileName: "20260822_173429_603f7aa0_AssetsBackup_2026-08-22.zip",
          fileSizeBytes: 980547,
          fileSizeFormatted: "0.94 MB",
          status: "Completed",
          checksumSHA256:
            "59D665324B6B76C37F1E51BB71E59C19A7DF40DB49222E457923B650DDD227FE",
          sourceClient: "C:\\AutoBackups\\2026-08\\AssetsBackup_2026-08-22.zip",
          receivedAt: "2026-08-22T17:34:53.506099Z",
        },
        {
          id: 268,
          fileName: "DatabaseBackup_2026-08-22.zip",
          fileSizeBytes: 295302363,
          fileSizeFormatted: "281.62 MB",
          status: "Completed",
          checksumSHA256:
            "D74FB0DF2BDB166FCFDADBF15F7A2196AF6ACF7149AB76196DD1DAFCD5D0A312",
          sourceClient:
            "C:\\AutoBackups\\80.3\\Database\\DatabaseBackup_2026-08-22.zip",
          receivedAt: "2026-08-22T17:34:53.169124Z",
        },
      ],
    },
    {
      date: "2026-08-23",
      displayDate: "23 Aug",
      cleanCount: 18,
      infectedCount: 0,
      failedCount: 0,
      totalCount: 18,
      sizeBytes: 2716549497,
      sizeFormatted: "2.53 GB",
      sizeInMB: 2590.7,
      sizeInGB: 2.53,
      records: [
        {
          id: 288,
          fileName: "DatabaseBackup_2026-08-23.zip",
          fileSizeBytes: 295685828,
          fileSizeFormatted: "281.99 MB",
          status: "Completed",
          checksumSHA256:
            "00C2B3546D24560853B2FAD92B55B2FFFE0E06112963F23F993F1C28AC8D1DE0",
          sourceClient: "C:\\AutoBackups\\80.3\\DatabaseBackup_2026-08-23.zip",
          receivedAt: "2026-08-23T17:35:07.452589Z",
        },
      ],
    },
    {
      date: "2026-08-24",
      displayDate: "24 Aug",
      cleanCount: 10,
      infectedCount: 0,
      failedCount: 0,
      totalCount: 10,
      sizeBytes: 61577922,
      sizeFormatted: "58.73 MB",
      sizeInMB: 58.73,
      sizeInGB: 0.057,
      records: [
        {
          id: 298,
          fileName: "DatabaseBackup_2026-08-24.zip",
          fileSizeBytes: 6157920,
          fileSizeFormatted: "5.87 MB",
          status: "Completed",
          checksumSHA256:
            "DADF833807C604D2BBC457CED168161D206D7607E33B959BB128805DE7165227",
          sourceClient: "80.13",
          receivedAt: "2026-08-24T06:57:15.779274Z",
        },
      ],
    },
  ],
};

export default function BackupDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(INITIAL_MOCK_DATA);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Backup Server Authentication state (/api/v1/auth/login)
  const [backupToken, setBackupToken] = useState(
    () => localStorage.getItem("backup_server_jwt_token") || "",
  );
  const [backupUser, setBackupUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Analytics API state (/api/v1/backups/analytics)
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [analyticsData, setAnalyticsData] = useState(INITIAL_ANALYTICS_DATA);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [hoveredBarIndex, setHoveredBarIndex] = useState(null);

  // Inspector Modal state for Daily Records
  const [selectedDayRecord, setSelectedDayRecord] = useState(null);
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [expandedRecordId, setExpandedRecordId] = useState(null);

  // Search & Filter states for main tables
  const [clientSearch, setClientSearch] = useState("");
  const [activitySearch, setActivitySearch] = useState("");
  const [activityStatusFilter, setActivityStatusFilter] = useState("all");

  // Profile API Function (/api/v1/auth/profile)
  const fetchUserProfile = useCallback(async (authToken) => {
    if (!authToken) return null;
    try {
      const res = await axios.get(`${backupServer}/auth/profile`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 6000,
      });

      if (res.data && res.data.success && res.data.data) {
        setBackupUser(res.data.data);
        return res.data.data;
      }
    } catch (err) {
      console.warn(
        "Failed to fetch profile from /api/v1/auth/profile:",
        err.message,
      );
      setBackupUser(null);
    }
    return null;
  }, []);

  // Login Function to Backup Server (/api/v1/auth/login) - Requires explicit credentials
  const loginToBackupServer = useCallback(
    async (username, password) => {
      const targetUser = username || loginUsername;
      const targetPass = password || loginPassword;

      if (!targetUser || !targetPass) {
        toast.error("Please enter both username and password.");
        return null;
      }

      setAuthLoading(true);
      try {
        const res = await axios.post(
          `${backupServer}/auth/login`,
          { username: targetUser, password: targetPass },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            timeout: 8000,
          },
        );

        if (res.data && res.data.token) {
          const newToken = res.data.token;
          setBackupToken(newToken);
          localStorage.setItem("backup_server_jwt_token", newToken);

          // Fetch user profile using GET /api/v1/auth/profile
          const userProfile = await fetchUserProfile(newToken);

          if (userProfile) {
            toast.success(
              `Authenticated successfully as ${userProfile.fullName || username}`,
            );
            setShowLoginModal(false);
            return newToken;
          } else {
            toast.error("User profile verification failed");
          }
        } else {
          toast.error("Login failed: Invalid credentials or token missing");
        }
      } catch (err) {
        console.warn("Backup Server Auth failed:", err.message);
        toast.error(
          `Authentication failed: ${err.response?.data?.message || err.message}`,
        );
      } finally {
        setAuthLoading(false);
      }
      return null;
    },
    [loginUsername, loginPassword, fetchUserProfile],
  );

  // Logout from Backup Server
  const logoutBackupServer = useCallback(() => {
    localStorage.removeItem("backup_server_jwt_token");
    setBackupToken("");
    setBackupUser(null);
    toast.info("Logged out from Backup Server");
    setShowLoginModal(true);
  }, []);

  // Check existing token on Mount and validate profile
  useEffect(() => {
    const initAuth = async () => {
      const existingToken = localStorage.getItem("backup_server_jwt_token");
      if (existingToken) {
        setBackupToken(existingToken);
        const profile = await fetchUserProfile(existingToken);
        if (!profile) {
          localStorage.removeItem("backup_server_jwt_token");
          setBackupToken("");
          setBackupUser(null);
          setShowLoginModal(true);
        }
      } else {
        setShowLoginModal(true);
      }
    };
    initAuth();
  }, [fetchUserProfile]);

  // Fetch Dashboard API (/api/v1/backups/dashboard) - requires backupToken
  const fetchDashboardData = useCallback(
    async (isSilent = false) => {
      const activeToken =
        backupToken || localStorage.getItem("backup_server_jwt_token");
      if (!activeToken) return;

      if (!isSilent) setLoading(true);
      try {
        const authHeader = { Authorization: `Bearer ${activeToken}` };
        let response;
        try {
          response = await axios.get(`${backupServer}/backups/dashboard`, {
            headers: authHeader,
            params: { days },
            timeout: 6000,
          });
        } catch (backupErr) {
          if (
            backupErr.response &&
            (backupErr.response.status === 401 ||
              backupErr.response.status === 403)
          ) {
            logoutBackupServer();
            return;
          }
          if (!response) {
            try {
              response = await axios.get(`${server}/backups/dashboard`, {
                headers: authHeader,
                params: { days },
                timeout: 5000,
              });
            } catch (primaryErr) {
              response = await axios.get(
                `http://localhost:8585/api/v1/backups/dashboard`,
                {
                  headers: authHeader,
                  params: { days },
                  timeout: 3000,
                },
              );
            }
          }
        }

        if (response && response.data) {
          setData(response.data);
          setLastUpdated(new Date());
          if (!isSilent) toast.success("Dashboard metrics updated");
        }
      } catch (err) {
        console.warn("Using fallback backup dataset:", err.message);
        setData(INITIAL_MOCK_DATA);
        setLastUpdated(new Date());
      } finally {
        if (!isSilent) setLoading(false);
      }
    },
    [days, backupToken, logoutBackupServer],
  );

  // Fetch Analytics API (/api/v1/backups/analytics) - requires backupToken
  const fetchAnalyticsData = useCallback(
    async (isSilent = false) => {
      const activeToken =
        backupToken || localStorage.getItem("backup_server_jwt_token");
      if (!activeToken) return;

      if (!isSilent) setAnalyticsLoading(true);
      try {
        const authHeader = { Authorization: `Bearer ${activeToken}` };
        const queryParams = { days: analyticsDays };
        if (selectedClientId && selectedClientId.trim() !== "") {
          queryParams.clientId = selectedClientId.trim();
        }

        let response;
        try {
          response = await axios.get(`${backupServer}/backups/analytics`, {
            headers: authHeader,
            params: queryParams,
            timeout: 6000,
          });
        } catch (err1) {
          if (
            err1.response &&
            (err1.response.status === 401 || err1.response.status === 403)
          ) {
            logoutBackupServer();
            return;
          }
          if (!response) {
            try {
              response = await axios.get(`${server}/backups/analytics`, {
                headers: authHeader,
                params: queryParams,
                timeout: 5000,
              });
            } catch (err2) {
              response = await axios.get(
                `https://backupserver.appdeploy.in/api/v1/backups/analytics`,
                {
                  headers: authHeader,
                  params: queryParams,
                  timeout: 4000,
                },
              );
            }
          }
        }

        if (response && response.data) {
          setAnalyticsData(response.data);
          if (!isSilent) toast.success("Analytics trend refreshed");
        }
      } catch (err) {
        console.warn("Using fallback analytics dataset:", err.message);
        setAnalyticsData(INITIAL_ANALYTICS_DATA);
      } finally {
        if (!isSilent) setAnalyticsLoading(false);
      }
    },
    [analyticsDays, selectedClientId, backupToken, logoutBackupServer],
  );

  useEffect(() => {
    if (backupUser && backupToken) {
      fetchDashboardData();
    }
  }, [days, backupUser, backupToken, fetchDashboardData]);

  useEffect(() => {
    if (backupUser && backupToken) {
      fetchAnalyticsData();
    }
  }, [
    analyticsDays,
    selectedClientId,
    backupUser,
    backupToken,
    fetchAnalyticsData,
  ]);

  // Auto-refresh timer
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchDashboardData(true);
        fetchAnalyticsData(true);
      }, 15000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchDashboardData, fetchAnalyticsData]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info("Copied to clipboard!", { autoClose: 2000 });
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getTimeAgo = (isoString) => {
    if (!isoString) return "N/A";
    const diffMs = new Date() - new Date(isoString);
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Safe extractions
  const sysHealth = data?.systemHealth || INITIAL_MOCK_DATA.systemHealth;
  const kpi = data?.kpi || INITIAL_MOCK_DATA.kpi;
  const threatIntel =
    data?.threatIntelligence || INITIAL_MOCK_DATA.threatIntelligence;
  const clientNodes =
    data?.clientNodeHealth || INITIAL_MOCK_DATA.clientNodeHealth;
  const heatmap = data?.hourlyHeatmap || INITIAL_MOCK_DATA.hourlyHeatmap;
  const timeSeries = data?.timeSeriesTrend || INITIAL_MOCK_DATA.timeSeriesTrend;
  const recentActivity =
    data?.recentActivity || INITIAL_MOCK_DATA.recentActivity;

  const analyticsChartData =
    analyticsData?.chartData || INITIAL_ANALYTICS_DATA.chartData;
  const analyticsSummary =
    analyticsData?.summary || INITIAL_ANALYTICS_DATA.summary;

  const diskStorage = sysHealth?.serverDiskStorage || {};
  const usedPercentageVal = parseFloat(
    (diskStorage.usedPercentage || "0").replace("%", ""),
  );

  // Filtered Client Nodes
  const filteredClients = (clientNodes.clientAnalytics || []).filter((client) =>
    client.clientIdentifier.toLowerCase().includes(clientSearch.toLowerCase()),
  );

  // Filtered Recent Activity
  const filteredActivities = (recentActivity || []).filter((item) => {
    const matchesSearch =
      item.fileName.toLowerCase().includes(activitySearch.toLowerCase()) ||
      item.sourceClient.toLowerCase().includes(activitySearch.toLowerCase());
    const matchesStatus =
      activityStatusFilter === "all" ||
      item.status.toLowerCase() === activityStatusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  // Filtered records inside Modal
  const modalRecords = useMemo(() => {
    if (!selectedDayRecord || !selectedDayRecord.records) return [];
    if (!modalSearchQuery.trim()) return selectedDayRecord.records;
    const query = modalSearchQuery.toLowerCase().trim();
    return selectedDayRecord.records.filter(
      (rec) =>
        rec.fileName.toLowerCase().includes(query) ||
        (rec.checksumSHA256 &&
          rec.checksumSHA256.toLowerCase().includes(query)) ||
        (rec.sourceClient && rec.sourceClient.toLowerCase().includes(query)) ||
        rec.id.toString().includes(query),
    );
  }, [selectedDayRecord, modalSearchQuery]);

  // Custom Glassmorphic Tooltip for the upgraded Bar Chart
  const CustomAnalyticsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="bg-white text-slate-800 p-4 rounded-xl border border-blue-200 shadow-2xl space-y-2 text-xs min-w-[220px]">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="font-bold text-blue-700 flex items-center gap-1.5">
              <MdOutlineCalendarToday className="text-xs text-blue-600" />
              {dataPoint.displayDate || label} ({dataPoint.date})
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              {dataPoint.cleanCount || 0} Clean
            </span>
          </div>

          <div className="space-y-1 text-[11px]">
            <div className="flex justify-between">
              <span className="text-slate-500">Total Archives:</span>
              <span className="font-bold text-slate-900">
                {dataPoint.totalCount || dataPoint.cleanCount || 0} files
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Storage Size:</span>
              <span className="font-bold text-purple-700">
                {dataPoint.sizeFormatted || `${dataPoint.sizeInMB} MB`}
              </span>
            </div>
            {dataPoint.infectedCount > 0 && (
              <div className="flex justify-between text-rose-600 font-semibold">
                <span>Infected Threats:</span>
                <span>{dataPoint.infectedCount}</span>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-slate-100 text-[10px] text-blue-600 flex items-center justify-between font-medium">
            <span>✨ Click bar to inspect records</span>
            <span className="text-slate-400">→</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-4 md:p-6 space-y-6 text-slate-800 font-sans">
      {/* Header Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-slate-200/80">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/20">
              <MdCloudDone className="text-2xl" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                  Backup Dashboard & Analytics
                </h1>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    backupUser
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-rose-50 text-rose-700 border border-rose-200"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${backupUser ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`}
                  ></span>
                  {backupUser
                    ? sysHealth.status || "Optimal"
                    : "Locked / Auth Required"}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                <span>
                  Last synced: {lastUpdated.toLocaleTimeString()} (
                  {getTimeAgo(sysHealth.lastBackupReceivedAt)})
                </span>
                <span>•</span>
                <span>Dashboard Window: {days} Days</span>
              </p>
            </div>
          </div>
        </div>

        {/* Controls: Days Filter, Auto Refresh, Manual Refresh, Auth Status */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Dashboard Days Selector */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-medium text-slate-600">
            {[7, 14, 30, 60, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  days === d
                    ? "bg-white text-blue-700 shadow-sm font-semibold"
                    : "hover:text-slate-900"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>

          {/* Auto Refresh Toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
              autoRefresh
                ? "bg-blue-50 text-blue-700 border-blue-200"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            <MdOutlineCloudSync
              className={`text-base ${autoRefresh ? "animate-spin" : ""}`}
            />
            {autoRefresh ? "Live Sync ON" : "Live Sync OFF"}
          </button>

          {/* Backup Server Auth Status Pill / Login / Logout Button */}
          {backupUser ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl text-xs font-semibold transition-all shadow-sm"
                title="Click to view Backup Server profile session details"
              >
                <MdVerified className="text-sm text-blue-600" />
                <span>
                  Session: {backupUser.fullName || backupUser.username}
                </span>
              </button>

              <button
                onClick={logoutBackupServer}
                className="px-3 py-2 bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-700 border border-slate-200 hover:border-rose-200 rounded-xl text-xs font-semibold transition-all"
                title="Logout from Backup Server"
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowLoginModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 active:scale-95 transition-all"
            >
              <MdLockOutline className="text-base" />
              Login to Backup Server
            </button>
          )}

          {/* Refresh Button */}
          {backupUser && (
            <button
              onClick={() => {
                fetchDashboardData();
                fetchAnalyticsData();
              }}
              disabled={loading || analyticsLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-semibold shadow-md shadow-blue-600/20 hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
            >
              <MdRefresh
                className={`text-base ${
                  loading || analyticsLoading ? "animate-spin" : ""
                }`}
              />
              Refresh All
            </button>
          )}
        </div>
      </div>

      {(loading || analyticsLoading) && <LoadingComponent />}

      {!backupUser ? (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 md:p-12 rounded-3xl border border-slate-200 shadow-sm text-center max-w-2xl mx-auto space-y-5 my-8"
        >
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto border border-blue-100 shadow-inner">
            <MdLockOutline className="text-3xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Backup & Security Sub-Modules Locked
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Authentication is required to view live metrics, storage velocity,
              SHA-256 checksums, client node health, and audit logs.
            </p>
          </div>

          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 text-xs text-slate-600 space-y-1.5 font-mono text-left max-w-md mx-auto">
            <div className="flex justify-between">
              <span className="text-slate-400">Login Endpoint:</span>
              <span className="font-semibold text-slate-800">
                POST /api/v1/auth/login
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Profile Endpoint:</span>
              <span className="font-semibold text-slate-800">
                GET /api/v1/auth/profile
              </span>
            </div>
          </div>

          <button
            onClick={() => setShowLoginModal(true)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/20 active:scale-95 inline-flex items-center gap-2"
          >
            <MdLockOutline className="text-base" />
            Login to Access Backup Dashboard
          </button>
        </motion.div>
      ) : (
        <>
          {/* Top Section: System Health & Server Storage */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* System Health Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200/80 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <MdOutlineSecurity className="text-blue-600 text-lg" />{" "}
                    System Security & Scanner
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                    <MdCheckCircle className="text-emerald-600" />
                    Active
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-baseline gap-2">
                    {sysHealth.threatRiskScore}{" "}
                    <span className="text-xs font-medium text-slate-500">
                      Threat Risk Score
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Real-time automated antivirus scanner is active and
                    monitoring incoming backup packages.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-slate-500 block">
                    Threats Intercepted
                  </span>
                  <span className="text-base font-bold text-emerald-600">
                    {threatIntel.totalThreatsIntercepted || 0}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Last Backup</span>
                  <span className="text-xs font-semibold text-slate-900 truncate block">
                    {getTimeAgo(sysHealth.lastBackupReceivedAt)}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Server Storage Capacity Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200/80 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <MdStorage className="text-blue-600 text-lg" /> Server
                    Storage ({diskStorage.driveName || "C:\\"})
                  </span>
                  <span className="text-xs font-bold text-slate-700">
                    {diskStorage.usedPercentage || "0%"} Used
                  </span>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-xs text-slate-600 font-medium">
                    <span>Used Space</span>
                    <span className="font-bold text-slate-900">
                      {diskStorage.freeSpaceFormatted &&
                      diskStorage.totalSpaceFormatted
                        ? `${(
                            parseFloat(
                              (diskStorage.totalSpaceFormatted || "0").split(
                                " ",
                              )[0],
                            ) -
                            parseFloat(
                              (diskStorage.freeSpaceFormatted || "0").split(
                                " ",
                              )[0],
                            )
                          ).toFixed(2)} GB / ${diskStorage.totalSpaceFormatted}`
                        : diskStorage.usedPercentage}
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden p-0.5 border border-slate-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(usedPercentageVal, 3)}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className={`h-full rounded-full ${
                        usedPercentageVal > 85
                          ? "bg-rose-500"
                          : usedPercentageVal > 65
                            ? "bg-amber-500"
                            : "bg-blue-600"
                      }`}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-500 pt-1">
                    <span>Free: {diskStorage.freeSpaceFormatted}</span>
                    <span>Total: {diskStorage.totalSpaceFormatted}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                <span className="flex items-center gap-1 text-slate-500">
                  <MdOutlineSpeed className="text-blue-600 text-base" /> Daily
                  Velocity
                </span>
                <span className="font-bold text-slate-900">
                  {kpi.dailyStorageVelocityFormatted}
                </span>
              </div>
            </motion.div>

            {/* Active Nodes Summary Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200/80 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <MdComputer className="text-purple-600 text-lg" /> Client
                    Node Health
                  </span>
                  <span className="text-xs font-bold text-slate-700">
                    {clientNodes.totalNodes || 0} Total Nodes
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-xl">
                    <span className="text-[11px] font-medium text-emerald-700 block">
                      Online (24h)
                    </span>
                    <span className="text-2xl font-bold text-emerald-800 mt-1 block">
                      {clientNodes.onlineNodes24h || 0}
                    </span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200/80 p-3 rounded-xl">
                    <span className="text-[11px] font-medium text-slate-600 block">
                      Idle / Offline
                    </span>
                    <span className="text-2xl font-bold text-slate-700 mt-1 block">
                      {clientNodes.idleNodes || 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                <span className="text-slate-500">Active Clients Reporting</span>
                <span className="font-bold text-slate-900">
                  {kpi.activeClients}
                </span>
              </div>
            </motion.div>
          </div>

          {/* KPI Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              {
                label: "Total Backups",
                value: kpi.totalBackups,
                sub: `${kpi.activeClients} Active Nodes`,
                icon: RiFolderZipLine,
                color: "text-blue-600 bg-blue-50 border-blue-100",
              },
              {
                label: "Clean Backups",
                value: kpi.cleanBackups,
                sub: "100% Clean Rate",
                icon: RiShieldCheckLine,
                color: "text-emerald-600 bg-emerald-50 border-emerald-100",
              },
              {
                label: "Infected / Threats",
                value: kpi.infectedBackups,
                sub: `${kpi.threatDetectionRate} Detection`,
                icon: MdWarning,
                color:
                  kpi.infectedBackups > 0
                    ? "text-rose-600 bg-rose-50 border-rose-100"
                    : "text-slate-600 bg-slate-50 border-slate-200",
              },
              {
                label: "Failed Backups",
                value: kpi.failedBackups,
                sub: "Zero Errors",
                icon: MdErrorOutline,
                color:
                  kpi.failedBackups > 0
                    ? "text-amber-600 bg-amber-50 border-amber-100"
                    : "text-slate-600 bg-slate-50 border-slate-200",
              },
              {
                label: "Total Storage",
                value: kpi.totalStorageFormatted,
                sub: `Avg: ${kpi.averageBackupSizeFormatted}`,
                icon: MdStorage,
                color: "text-purple-600 bg-purple-50 border-purple-100",
              },
              {
                label: "Largest Backup",
                value: kpi.largestBackupSizeFormatted,
                sub: "Max Package Size",
                icon: MdOutlineSpeed,
                color: "text-indigo-600 bg-indigo-50 border-indigo-100",
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.05 * idx }}
                className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 truncate">
                    {item.label}
                  </span>
                  <div className={`p-2 rounded-lg border ${item.color}`}>
                    <item.icon className="text-base" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-xl font-extrabold text-slate-900 truncate">
                    {item.value}
                  </div>
                  <span className="text-[11px] font-medium text-slate-400 mt-0.5 block truncate">
                    {item.sub}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CLEAN & ATTRACTIVE BAR CHART SECTION: Analytics & Storage Growth */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-xl border border-blue-100">
                    <MdOutlineAnalytics className="text-xl" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 tracking-tight flex items-center gap-2">
                      Backup Analytics & Storage Velocity
                    </h2>
                    <p className="text-xs text-slate-500">
                      Period: {analyticsData?.startDate || "2026-08-18"} to{" "}
                      {analyticsData?.endDate || "2026-08-24"} (
                      {analyticsData?.periodDays || analyticsDays} Days)
                    </p>
                  </div>
                </div>
              </div>

              {/* Analytics Filters: Days Window & Client Filter */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Client Filter Dropdown */}
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 text-xs">
                  <MdFilterList className="text-blue-600 text-base" />
                  <span className="text-slate-500 font-medium">Node:</span>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="bg-transparent font-semibold text-slate-800 focus:outline-none cursor-pointer max-w-[180px] truncate"
                  >
                    <option value="">All Client Nodes</option>
                    {(clientNodes.clientAnalytics || []).map((c, i) => (
                      <option key={i} value={c.clientIdentifier}>
                        {c.clientIdentifier.length > 25
                          ? `...${c.clientIdentifier.slice(-20)}`
                          : c.clientIdentifier}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Analytics Days Selector */}
                <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-medium text-slate-600">
                  {[7, 14, 30, 90].map((d) => (
                    <button
                      key={d}
                      onClick={() => setAnalyticsDays(d)}
                      className={`px-3 py-1 rounded-lg transition-all ${
                        analyticsDays === d
                          ? "bg-blue-600 text-white shadow-sm font-semibold"
                          : "hover:text-slate-900"
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Analytics Summary Badges Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200/80 text-xs">
              <div className="flex items-center gap-3 border-r border-slate-200/80 pr-2">
                <div className="p-2 bg-blue-100 text-blue-700 rounded-xl border border-blue-200">
                  <RiFolderZipLine className="text-lg" />
                </div>
                <div>
                  <span className="text-slate-500 text-[11px] block font-medium">
                    Period Total Backups
                  </span>
                  <span className="text-xl font-bold text-slate-900">
                    {analyticsSummary.totalBackups || 0}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 border-r border-slate-200/80 pr-2">
                <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl border border-emerald-200">
                  <RiShieldCheckFill className="text-lg" />
                </div>
                <div>
                  <span className="text-slate-500 text-[11px] block font-medium">
                    Clean Backups
                  </span>
                  <span className="text-xl font-bold text-emerald-600">
                    {analyticsSummary.totalClean || 0}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 border-r border-slate-200/80 pr-2">
                <div className="p-2 bg-rose-100 text-rose-700 rounded-xl border border-rose-200">
                  <MdWarning className="text-lg" />
                </div>
                <div>
                  <span className="text-slate-500 text-[11px] block font-medium">
                    Threats Intercepted
                  </span>
                  <span className="text-xl font-bold text-rose-600">
                    {analyticsSummary.totalInfected || 0}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 text-purple-700 rounded-xl border border-purple-200">
                  <MdStorage className="text-lg" />
                </div>
                <div>
                  <span className="text-slate-500 text-[11px] block font-medium">
                    Storage Volume
                  </span>
                  <span className="text-xl font-bold text-purple-700">
                    {analyticsSummary.totalBytesFormatted || "0.00 B"}
                  </span>
                </div>
              </div>
            </div>

            {/* REFINED SLIM BAR & COMPOSED CHART */}
            <div className="h-72 w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={analyticsChartData}
                  margin={{ top: 15, right: 15, left: -15, bottom: 0 }}
                  onMouseMove={(state) => {
                    if (state && state.activeTooltipIndex !== undefined) {
                      setHoveredBarIndex(state.activeTooltipIndex);
                    }
                  }}
                  onMouseLeave={() => setHoveredBarIndex(null)}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fontSize: 11, fill: "#64748b", fontWeight: 600 }}
                    axisLine={{ stroke: "#e2e8f0" }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    label={{
                      value: "Backup Count",
                      angle: -90,
                      position: "insideLeft",
                      fontSize: 10,
                      fill: "#94a3b8",
                      fontWeight: 500,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11, fill: "#8b5cf6" }}
                    axisLine={{ stroke: "#cbd5e1" }}
                    label={{
                      value: "Size (MB)",
                      angle: 90,
                      position: "insideRight",
                      fontSize: 10,
                      fill: "#8b5cf6",
                      fontWeight: 500,
                    }}
                  />
                  <Tooltip content={<CustomAnalyticsTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
                  />

                  {/* Slim Solid Bar */}
                  <Bar
                    yAxisId="left"
                    dataKey="cleanCount"
                    name="Clean Backups Count"
                    barSize={35}
                    radius={[6, 6, 0, 0]}
                    onClick={(entry) => setSelectedDayRecord(entry)}
                    cursor="pointer"
                  >
                    {analyticsChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={hoveredBarIndex === index ? "#1d4ed8" : "#2563eb"}
                        className="transition-colors duration-200"
                      />
                    ))}
                  </Bar>

                  {/* Storage Growth Line */}
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="sizeInMB"
                    name="Storage Growth (MB)"
                    stroke="#8b5cf6"
                    strokeWidth={2.5}
                    dot={{
                      r: 4,
                      fill: "#8b5cf6",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 6,
                      fill: "#7c3aed",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Daily Records Click-to-Inspect Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs bg-blue-600 text-white p-3 rounded-xl shadow-sm">
              <span className="flex items-center gap-2 text-white font-medium">
                <MdOutlineFingerprint className="text-base text-blue-200" />
                Click any bar in the chart to open the SHA-256 Checksum
                Inspector for that day.
              </span>
              {analyticsChartData.some((d) => d.records?.length > 0) && (
                <button
                  onClick={() =>
                    setSelectedDayRecord(
                      analyticsChartData.find((d) => d.records?.length > 0),
                    )
                  }
                  className="px-3.5 py-1 bg-white text-blue-700 hover:bg-blue-50 rounded-lg font-semibold shadow-sm text-xs transition-all"
                >
                  Inspect Sample Records (
                  {
                    analyticsChartData.find((d) => d.records?.length > 0)
                      ?.displayDate
                  }
                  )
                </button>
              )}
            </div>
          </div>
          {/* Visual Charts: Time Series Trend & Hourly Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Time Series Trend Chart */}
            <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <RiHistoryLine className="text-blue-600 text-lg" /> Backup
                    Volume & Health Trend
                  </h3>
                  <p className="text-xs text-slate-500">
                    Daily backup counts categorized by Clean, Infected, and
                    Failed over time
                  </p>
                </div>
                <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
                  {timeSeries.length} Data Points
                </span>
              </div>

              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={timeSeries}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="cleanGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#2563eb"
                          stopOpacity={0.25}
                        />
                        <stop
                          offset="95%"
                          stopColor="#2563eb"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="infectedGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#e11d48"
                          stopOpacity={0.25}
                        />
                        <stop
                          offset="95%"
                          stopColor="#e11d48"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f1f5f9"
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#ffffff",
                        borderColor: "#e2e8f0",
                        borderRadius: "12px",
                        color: "#1e293b",
                        fontSize: "12px",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.08)",
                      }}
                      formatter={(val, name) => [val, name]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend
                      iconType="circle"
                      wrapperStyle={{ fontSize: "12px" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="cleanCount"
                      name="Clean Backups"
                      stroke="#2563eb"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#cleanGrad)"
                    />
                    <Area
                      type="monotone"
                      dataKey="infectedCount"
                      name="Infected"
                      stroke="#e11d48"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#infectedGrad)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Hourly Distribution Heatmap */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2 mb-1">
                  <RiTimeLine className="text-purple-600 text-lg" /> Hourly
                  Distribution
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Peak traffic times and backup counts per hour
                </p>

                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={heatmap}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f1f5f9"
                      />
                      <XAxis
                        dataKey="hour"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          borderColor: "#e2e8f0",
                          borderRadius: "12px",
                          color: "#1e293b",
                          fontSize: "12px",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.08)",
                        }}
                        formatter={(val, name, props) => [
                          `${val} backups (${props.payload.totalBytesFormatted})`,
                          "Volume",
                        ]}
                      />
                      <Bar
                        dataKey="backupCount"
                        name="Backups"
                        barSize={35}
                        fill="#6366f1"
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="mt-2 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>Extension Breakdown:</span>
                <span className="font-semibold text-slate-800">
                  {threatIntel.extensionBreakdown?.[0]?.extension || ".zip"} (
                  {threatIntel.extensionBreakdown?.[0]?.totalBytesFormatted ||
                    "5.12 GB"}
                  )
                </span>
              </div>
            </div>
          </div>

          {/* Client Node Analytics Table */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <MdComputer className="text-blue-600 text-lg" /> Client Node
                  Analytics
                </h3>
                <p className="text-xs text-slate-500">
                  Individual node backup health, online status (24h), and
                  storage stats
                </p>
              </div>

              {/* Client Search */}
              <div className="relative w-full md:w-72">
                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
                <input
                  type="text"
                  placeholder="Search client identifier or path..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Client Identifier</th>
                    <th className="py-3 px-4">Status (24h)</th>
                    <th className="py-3 px-4">Total Backups</th>
                    <th className="py-3 px-4">Health (Clean / Inf)</th>
                    <th className="py-3 px-4">Total Storage</th>
                    <th className="py-3 px-4">Last Backup</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {filteredClients.length > 0 ? (
                    filteredClients.map((client, idx) => {
                      const displayId =
                        client.clientIdentifier.length > 40
                          ? `...${client.clientIdentifier.slice(-35)}`
                          : client.clientIdentifier;

                      return (
                        <tr
                          key={idx}
                          className="hover:bg-slate-50/80 transition-colors"
                        >
                          <td className="py-3 px-4 font-mono font-medium text-slate-900">
                            <span title={client.clientIdentifier}>
                              {displayId}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {client.isOnline24h ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                Online
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                                Idle / Offline
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 font-bold text-slate-800">
                            {client.totalBackups}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-emerald-600 font-semibold">
                              {client.cleanBackups} clean
                            </span>
                            {client.infectedBackups > 0 && (
                              <span className="text-rose-600 font-semibold ml-2">
                                ({client.infectedBackups} infected)
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 font-semibold text-slate-800">
                            {client.totalBytesFormatted}
                          </td>
                          <td className="py-3 px-4 text-slate-500">
                            {formatDate(client.lastBackupAt)}
                          </td>
                          <td className="py-3 px-4 text-right flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedClientId(client.clientIdentifier);
                                toast.info(
                                  `Filtered analytics for ${displayId}`,
                                );
                              }}
                              className="px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-[11px] font-semibold transition-colors"
                              title="Filter Analytics by this client"
                            >
                              Filter Analytics
                            </button>
                            <button
                              onClick={() =>
                                copyToClipboard(client.clientIdentifier)
                              }
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Copy Full Identifier"
                            >
                              <MdContentCopy className="text-sm" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="py-8 text-center text-slate-400 text-xs"
                      >
                        No client nodes found matching search filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Backup Activity Log */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <RiHistoryLine className="text-emerald-600 text-lg" /> Recent
                  Backup Activity Log
                </h3>
                <p className="text-xs text-slate-500">
                  Audit log of recently transmitted database and file archives
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {/* Status Filter */}
                <select
                  value={activityStatusFilter}
                  onChange={(e) => setActivityStatusFilter(e.target.value)}
                  className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="all">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="infected">Infected</option>
                </select>

                {/* Activity Search */}
                <div className="relative w-full md:w-64">
                  <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
                  <input
                    type="text"
                    placeholder="Search file name or client..."
                    value={activitySearch}
                    onChange={(e) => setActivitySearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">ID</th>
                    <th className="py-3 px-4">File Name</th>
                    <th className="py-3 px-4">Size</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Source Client / IP</th>
                    <th className="py-3 px-4">Received Timestamp</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {filteredActivities.length > 0 ? (
                    filteredActivities.map((item) => {
                      const displayFileName =
                        item.fileName.length > 45
                          ? `...${item.fileName.slice(-40)}`
                          : item.fileName;

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-slate-50/80 transition-colors"
                        >
                          <td className="py-3 px-4 font-mono text-slate-400">
                            #{item.id}
                          </td>
                          <td className="py-3 px-4 font-medium text-slate-900 font-mono">
                            <span title={item.fileName}>{displayFileName}</span>
                          </td>
                          <td className="py-3 px-4 font-semibold text-slate-800">
                            {item.fileSizeFormatted}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                                item.status === "Completed"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : item.status === "Infected"
                                    ? "bg-rose-50 text-rose-700 border-rose-200"
                                    : "bg-amber-50 text-amber-700 border-amber-200"
                              }`}
                            >
                              <MdCheckCircle className="text-xs" />
                              {item.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-mono text-slate-600">
                            {item.sourceClient}
                          </td>
                          <td className="py-3 px-4 text-slate-500">
                            {formatDate(item.receivedAt)}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => copyToClipboard(item.fileName)}
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Copy File Name"
                            >
                              <MdContentCopy className="text-sm" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="py-8 text-center text-slate-400 text-xs"
                      >
                        No activity logs found matching filter criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* UPGRADED BAR CLICK MODAL: Daily Backup Records & SHA-256 Checksums */}
          <AnimatePresence>
            {selectedDayRecord && (
              <div className="fixed inset-0 z-50 bg-slate-800/40 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 15 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]"
                >
                  {/* Premium Blue Gradient Header */}
                  <div className="p-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-blue-500/30 shadow-md">
                    <div className="flex items-center gap-3.5">
                      <div className="p-3 bg-white/20 text-white rounded-2xl backdrop-blur-md shadow-inner">
                        <MdOutlineFingerprint className="text-2xl text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold tracking-tight text-white">
                            Daily Backup Archive Inspector
                          </h3>
                          <span className="px-3 py-1 bg-white/20 text-white border border-white/30 text-xs font-bold rounded-full flex items-center gap-1 backdrop-blur-md">
                            <MdOutlineCalendarToday className="text-xs" />
                            {selectedDayRecord.displayDate ||
                              selectedDayRecord.date}
                          </span>
                        </div>
                        <p className="text-xs text-blue-100 mt-1">
                          Verification period: {selectedDayRecord.date || "N/A"}{" "}
                          | Standard SHA-256 Hash Auditing
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedDayRecord(null);
                        setModalSearchQuery("");
                      }}
                      className="p-2 text-blue-100 hover:text-white hover:bg-white/20 rounded-2xl transition-all self-start sm:self-center"
                    >
                      <MdClose className="text-2xl" />
                    </button>
                  </div>

                  {/* In-Modal Quick Stats & Search Bar */}
                  <div className="p-5 bg-slate-50 border-b border-slate-200/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Stats Summary Pills */}
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <div className="bg-white px-3.5 py-2 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
                        <RiStackLine className="text-blue-600 text-base" />
                        <div>
                          <span className="text-slate-500 text-[10px] block uppercase font-semibold">
                            Total Records
                          </span>
                          <span className="font-bold text-slate-900">
                            {selectedDayRecord.totalCount ||
                              selectedDayRecord.records?.length ||
                              0}{" "}
                            Files
                          </span>
                        </div>
                      </div>

                      <div className="bg-white px-3.5 py-2 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
                        <MdStorage className="text-purple-600 text-base" />
                        <div>
                          <span className="text-slate-500 text-[10px] block uppercase font-semibold">
                            Day Storage Size
                          </span>
                          <span className="font-bold text-purple-700">
                            {selectedDayRecord.sizeFormatted ||
                              `${selectedDayRecord.sizeInMB || 0} MB`}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white px-3.5 py-2 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
                        <RiShieldCheckFill className="text-emerald-600 text-base" />
                        <div>
                          <span className="text-slate-500 text-[10px] block uppercase font-semibold">
                            Clean Status
                          </span>
                          <span className="font-bold text-emerald-700">
                            100% Clean
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Modal Search Bar */}
                    <div className="relative w-full md:w-72">
                      <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
                      <input
                        type="text"
                        placeholder="Search file name, SHA-256 hash or path..."
                        value={modalSearchQuery}
                        onChange={(e) => setModalSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                      />
                      {modalSearchQuery && (
                        <button
                          onClick={() => setModalSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Modal Body Container */}
                  <div className="p-6 overflow-y-auto flex-1 space-y-4 bg-slate-100/50">
                    {modalRecords.length > 0 ? (
                      modalRecords.map((rec, index) => {
                        const isExpanded = expandedRecordId === rec.id;

                        return (
                          <motion.div
                            key={rec.id || index}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.04 }}
                            className="bg-white rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition-all overflow-hidden border-l-4 border-l-emerald-500"
                          >
                            {/* Record Main Row */}
                            <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="p-2.5 bg-blue-50 text-blue-700 rounded-xl border border-blue-100 mt-0.5">
                                  <RiFolderZipLine className="text-xl" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-mono text-[10px] font-bold rounded">
                                      ID #{rec.id}
                                    </span>
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 font-mono text-[10px] font-semibold rounded">
                                      .ZIP ARCHIVE
                                    </span>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <MdCheckCircle className="text-xs" />
                                      {rec.status}
                                    </span>
                                  </div>
                                  <h4
                                    className="font-mono text-xs font-bold text-slate-900 mt-1 truncate cursor-pointer hover:text-blue-600 transition-colors"
                                    title={rec.fileName}
                                    onClick={() =>
                                      setExpandedRecordId(
                                        isExpanded ? null : rec.id,
                                      )
                                    }
                                  >
                                    {rec.fileName}
                                  </h4>
                                  <p className="text-[11px] text-slate-500 mt-0.5 truncate font-mono">
                                    Source: {rec.sourceClient}
                                  </p>
                                </div>
                              </div>

                              {/* Record Size & Actions */}
                              <div className="flex items-center gap-3 self-end md:self-center border-t md:border-t-0 pt-2 md:pt-0 border-slate-100">
                                <div className="text-right">
                                  <span className="text-xs font-black text-slate-900 block">
                                    {rec.fileSizeFormatted}
                                  </span>
                                  <span className="text-[10px] text-slate-400 block font-mono">
                                    {(rec.fileSizeBytes || 0).toLocaleString()}{" "}
                                    Bytes
                                  </span>
                                </div>

                                <button
                                  onClick={() => copyToClipboard(rec.fileName)}
                                  className="p-2 bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded-xl text-xs transition-colors"
                                  title="Copy File Name"
                                >
                                  <MdContentCopy />
                                </button>

                                <button
                                  onClick={() =>
                                    setExpandedRecordId(
                                      isExpanded ? null : rec.id,
                                    )
                                  }
                                  className="p-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl text-xs transition-colors font-semibold flex items-center gap-1"
                                >
                                  {isExpanded ? (
                                    <MdExpandLess />
                                  ) : (
                                    <MdExpandMore />
                                  )}
                                  <span>{isExpanded ? "Less" : "Details"}</span>
                                </button>
                              </div>
                            </div>

                            {/* Expandable Details Box */}
                            {isExpanded && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="p-4 bg-blue-50/70 text-slate-800 border-t border-blue-100 space-y-3"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                                  <div>
                                    <span className="text-blue-900/70 text-[10px] uppercase font-bold block">
                                      Received Timestamp
                                    </span>
                                    <span className="text-slate-800 font-mono">
                                      {formatDate(rec.receivedAt)} (
                                      {getTimeAgo(rec.receivedAt)})
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-blue-900/70 text-[10px] uppercase font-bold block">
                                      Security Audit Status
                                    </span>
                                    <span className="text-emerald-700 font-semibold flex items-center gap-1">
                                      <MdVerified /> Antivirus Scanned & Passed
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-blue-900/70 text-[10px] uppercase font-bold block">
                                      Client IP / Source
                                    </span>
                                    <span className="text-slate-800 font-mono truncate block">
                                      {rec.sourceClient}
                                    </span>
                                  </div>
                                </div>

                                {/* SHA-256 Checksum Inspection Panel */}
                                {rec.checksumSHA256 && (
                                  <div className="bg-white p-3.5 rounded-xl border border-blue-200 shadow-sm space-y-1.5 font-mono">
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="text-blue-700 font-bold flex items-center gap-1.5">
                                        <MdLockOutline className="text-sm" />
                                        SHA-256 Cryptographic Checksum
                                      </span>
                                      <span className="text-emerald-700 text-[10px] font-sans font-semibold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                        Verified Intact
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3 bg-slate-100 p-2.5 rounded-lg border border-slate-200 text-slate-900 text-xs">
                                      <span className="truncate selection:bg-blue-500 selection:text-white font-mono">
                                        {rec.checksumSHA256}
                                      </span>
                                      <button
                                        onClick={() =>
                                          copyToClipboard(rec.checksumSHA256)
                                        }
                                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded font-sans text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm shrink-0"
                                      >
                                        <MdContentCopy /> Copy Hash
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </motion.div>
                        );
                      })
                    ) : (
                      <div className="py-16 text-center text-slate-400 space-y-2">
                        <MdOutlineInfo className="text-3xl text-slate-300 mx-auto" />
                        <p className="text-sm font-semibold text-slate-600">
                          No records matched your search query "
                          {modalSearchQuery}".
                        </p>
                        <p className="text-xs text-slate-400">
                          Try searching for a different file name or SHA-256
                          hash.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs">
                    <span className="text-slate-500 font-mono">
                      Displaying {modalRecords.length} of{" "}
                      {selectedDayRecord.records?.length || 0} records
                    </span>
                    <button
                      onClick={() => {
                        setSelectedDayRecord(null);
                        setModalSearchQuery("");
                      }}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20"
                    >
                      Close Inspector
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* MANUAL BACKUP SERVER LOGIN MODAL */}
      <AnimatePresence>
        {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-slate-800/40 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
            >
              <div className="p-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-md">
                    <MdLockOutline className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">
                      Backup Server Authentication
                    </h3>
                    <p className="text-sm text-blue-100">Secure Login</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="p-1.5 text-blue-100 hover:text-white hover:bg-white/20 rounded-xl transition-all"
                >
                  <MdClose className="text-xl" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  loginToBackupServer(loginUsername, loginPassword);
                }}
                className="p-5 space-y-4 text-xs"
              >
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Username / Email
                  </label>
                  <input
                    type="text"
                    required
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl font-sans text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    placeholder="Enter username or email"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl font-sans text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    placeholder="Enter password"
                  />
                </div>

                {backupToken && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 space-y-1">
                    <span className="font-bold block flex items-center gap-1">
                      <MdCheckCircle className="text-emerald-600" /> Active
                      Session Token
                    </span>
                    <p className="font-mono text-[10px] truncate opacity-90">
                      {backupToken}
                    </p>
                  </div>
                )}

                <div className="pt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowLoginModal(false)}
                    className="px-4 py-3 border border-gray-300 text-base bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={authLoading}
                    className="px-5 py-3 text-base bg-blue-600 border border-blue-700 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-md shadow-blue-600/20 flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {authLoading ? (
                      <MdRefresh className="animate-spin text-sm" />
                    ) : (
                      <MdVerified className="text-sm" />
                    )}
                    {authLoading ? "Authenticating..." : "Login & Save Token"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
